import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AssessmentIcon from "@mui/icons-material/Assessment";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LogoutIcon from "@mui/icons-material/Logout";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ProfileImage from "./Avatar";
import BankLogo from "../assets/Logo.png";
import useLogout from "./useLogout";

const GREEN = "#17B859";

// Requests come grouped by branch; the badge counts the requests themselves
export const countRequests = (groups) =>
  (Array.isArray(groups) ? groups : []).reduce(
    (sum, group) => sum + (group?.request?.length || 0),
    0,
  );

function NavItem({ item, nested, onNavigate }) {
  const { pathname } = useLocation();
  const selected = pathname === item.to;
  return (
    <ListItemButton
      selected={selected}
      onClick={() => onNavigate(item.to)}
      sx={{
        borderRadius: 2,
        mx: 1,
        mb: 0.5,
        pl: nested ? 3 : 1.5,
        py: 0.9,
        color: "#fff",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
        "&.Mui-selected, &.Mui-selected:hover": {
          background: `linear-gradient(90deg, ${GREEN}, rgba(23,184,89,0.6))`,
          color: "#08120c",
          "& .MuiListItemIcon-root": { color: "#08120c" },
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: "inherit" }}>
        {item.badge ? (
          <Badge badgeContent={item.badge} color="error" max={99}>
            {item.icon}
          </Badge>
        ) : (
          item.icon
        )}
      </ListItemIcon>
      <ListItemText
        primary={item.text}
        primaryTypographyProps={{
          sx: { fontSize: 13, fontWeight: selected ? 800 : 600 },
        }}
      />
    </ListItemButton>
  );
}

// The menu, drawn inside the layout's side column or its phone drawer
export default function SideBar({ onNavigate }) {
  const user = JSON.parse(secureLocalStorage.getItem("profile") || "null");
  const isAdmin = user?.results?.stock === "Administrator";
  const isApprover = user?.results?.stock === "Approver";
  const myId = user?.results?._id;
  const branch = user?.results?.branch || "";
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logout = useLogout();
  const requestGroups = useSelector((state) => state.locationRequest);
  const pendingIssues = useSelector((state) => state.received);
  const requestCount = countRequests(requestGroups);
  const requisitions = useSelector((state) => state.requisition);
  // nobody reviews their own, so those don't count as waiting for them
  const approvalCount = (requisitions || []).filter(
    (r) => r.status === "PENDING" && String(r.requestedById) !== String(myId),
  ).length;

  const consolList = useMemo(
    () => [
      { text: "Stock", to: "/stock/consol/report", icon: <AnalyticsIcon /> },
      { text: "Issues", to: "/stock/consol/issue/report", icon: <AnalyticsIcon /> },
      { text: "Receipts", to: "/stock/consol/receipt/report", icon: <AnalyticsIcon /> },
      { text: "Location Statement", to: "/stock/location-statement", icon: <AnalyticsIcon /> },
    ],
    [],
  );
  const [openConsol, setOpenConsol] = useState(
    consolList.some((item) => item.to === pathname),
  );
  const [confirmLogout, setConfirmLogout] = useState(false);

  const list = useMemo(() => {
    const approvals = {
      text: "Requisition Approvals",
      to: "/stock/requisition-approvals",
      icon: <FactCheckIcon />,
      badge: approvalCount,
    };
    if (isApprover) return [approvals];
    const items = [
      { text: "Dashboard", to: "/stock/", icon: <DashboardIcon /> },
      { text: "Stock", to: "/stock/stock-home", icon: <InventoryIcon /> },
      { text: "Store Report", to: "/stock/report", icon: <AssessmentIcon /> },
    ];
    if (isAdmin) {
      items.push(
        { text: "Requisition Report", to: "/stock/requisition-report", icon: <AssessmentIcon /> },
        { text: "Locations Report", to: "/stock/location-report", icon: <AssessmentIcon /> },
        {
          text: "Requests",
          to: "/stock/all-request",
          icon: <AssignmentTurnedInIcon />,
          badge: requestCount,
        },
        approvals,
        { text: "Forecast", to: "/stock/forecast", icon: <TrendingUpIcon /> },
      );
    } else {
      items.push({
        text: "Items to Confirm",
        to: `/stock/${branch}/new-items`,
        icon: <MoveToInboxIcon />,
        badge: pendingIssues?.length || 0,
      });
    }
    items.push({ text: "New Requisition", to: "/stock/request", icon: <PlaylistAddIcon /> });
    return items;
  }, [isAdmin, isApprover, requestCount, approvalCount, pendingIssues, branch]);

  const go = (to) => {
    navigate(to);
    onNavigate?.();
  };

  return (
    <Box
      component="nav"
      aria-label="Stock menu"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        color: "#fff",
        background:
          "radial-gradient(1200px 500px at 0% 0%, rgba(23,184,89,0.18), transparent 55%), linear-gradient(180deg, #2f3f47, #233239)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 2 }}>
        <Box component="img" src={BankLogo} alt="" sx={{ width: 36, height: 30, objectFit: "contain" }} />
        <Typography sx={{ fontWeight: 900, fontSize: 18 }}>
          <Box component="span" sx={{ color: GREEN }}>ARB.</Box>{" "}
          <Box component="span" sx={{ fontStyle: "italic" }}>stock</Box>
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        <Typography
          sx={{ px: 2.5, pb: 1, fontSize: 11, opacity: 0.7, letterSpacing: "0.15em", textTransform: "uppercase" }}
        >
          Menu
        </Typography>
        <List dense disablePadding>
          {list.map((item) => (
            <NavItem key={item.to} item={item} onNavigate={go} />
          ))}

          {isAdmin && (
            <>
              <ListItemButton
                onClick={() => setOpenConsol((open) => !open)}
                aria-expanded={openConsol}
                sx={{
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  pl: 1.5,
                  py: 0.9,
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "#fff" }}>
                  <AnalyticsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Consolidated Reports"
                  primaryTypographyProps={{ sx: { fontSize: 13, fontWeight: 700 } }}
                />
                <ExpandMoreIcon
                  sx={{ transition: "transform 0.2s", transform: openConsol ? "rotate(180deg)" : "none" }}
                />
              </ListItemButton>
              <Collapse in={openConsol} timeout="auto">
                <List dense disablePadding>
                  {consolList.map((item) => (
                    <NavItem key={item.to} item={item} nested onNavigate={go} />
                  ))}
                </List>
              </Collapse>
            </>
          )}
        </List>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
      <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
        <ProfileImage />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }} noWrap>
            {`${user?.results?.firstName || ""} ${user?.results?.lastName || ""}`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, opacity: 0.85 }}>
            <LocationOnIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 11, textTransform: "capitalize" }} noWrap>
              {branch}
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Log out">
          <IconButton
            aria-label="Log out"
            onClick={() => setConfirmLogout(true)}
            sx={{ bgcolor: GREEN, "&:hover": { bgcolor: "#14a34f" } }}
          >
            <LogoutIcon sx={{ color: "#fff" }} fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog open={confirmLogout} onClose={() => setConfirmLogout(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Log out of all the bank's apps?</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmLogout(false)} sx={{ textTransform: "none" }}>
            Stay logged in
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              setConfirmLogout(false);
              logout();
            }}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#28373f", "&:hover": { bgcolor: "#1f2b31" } }}
          >
            Log out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
